import React from 'react';
import { YStack, XStack, H3, H4, SizableText, Separator, Card, Button, Spinner } from 'tamagui';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions } from 'react-native';
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import toTitleCase from '@hooks/toTitleCase';

const WordpressOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['wordpressProfile'],
    queryFn: async () => apis.getWordpressProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const { data: blogDetails, isLoading: isBlogLoading } = useQuery({
    queryKey: ['wordpressBlogDetails', data?.details?.url, data?.details?.id, data?.details?.password],
    queryFn: async () => apis.getWordPressBlogDetails().then(res => res.posts),
    enabled: !!data?.details?.url && !!data?.details?.id && !!data?.details?.password,
  });

  const authorCount = React.useMemo(() => {
    if (!blogDetails || !blogDetails.length) return 0;
    return new Set(blogDetails.map((post: any) => post.author)).size;
  }, [blogDetails]);

  const latestPostYear = React.useMemo(() => {
    if (!blogDetails || !blogDetails.length) return 0;
    return new Date(blogDetails[0].date).getFullYear();
  }, [blogDetails]);

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load WordPress site
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your WordPress site, or try removing and re-adding the integration.  
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#21759B" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading WordPress site...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {data?.details && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 60,
                    height: 60,
                    borderRadius: 30,
                    backgroundColor: '#21759B',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <MaterialCommunityIcons name="wordpress" size={32} color="white" />
                  </View>

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                      {toTitleCase(data.details.name || "WordPress Site")}
                    </H4>
                    <SizableText
                      size="$3"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      ID: {data.details.id || 'N/A'}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Statistics */}
                <XStack gap="$3" justifyContent="space-around" marginBottom="$4">
                  <Stat
                    icon="file-document"
                    label="Posts"
                    value={blogDetails?.length || 0}
                    theme={theme}
                  />
                  <Stat
                    icon="account-multiple"
                    label="Authors"
                    value={authorCount}
                    theme={theme}
                  />
                  <Stat
                    icon="calendar"
                    label="Latest"
                    value={latestPostYear || 0}
                    theme={theme}
                  />
                </XStack>

                {/* Site URL */}
                {data.details.url && (
                  <XStack alignItems="center" gap="$3" marginBottom="$4">
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="link" size={20} color="#21759B" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Site URL
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                        numberOfLines={1}
                      >
                        {data.details.url}
                      </SizableText>
                    </YStack>
                  </XStack>
                )}

                {/* Action Button */}
                <Button
                  backgroundColor="#21759B"
                  color="white"
                  onPress={() => {
                    if (data.details?.url) {
                      Linking.openURL(data.details.url).catch(err => console.error("Failed to open URL:", err));
                    }
                  }}
                  borderRadius={12}
                  paddingVertical="$3"
                  disabled={!data.details?.url}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={18} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      Visit WordPress Site
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    WordPress Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    You can now manage your WordPress content and posts directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

const Stat = ({ icon, label, value, theme }: { icon: string; label: string; value: number; theme: boolean }) => (
  <YStack alignItems="center" gap="$2" flex={1}>
    <View style={{
      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
      borderRadius: 12,
      padding: 10
    }}>
      <MaterialCommunityIcons name={icon as any} size={24} color="#21759B" />
    </View>
    <SizableText
      fontSize="$6"
      fontWeight="700"
      color={theme ? "#1e293b" : "#f1f5f9"}
    >
      {value}
    </SizableText>
    <SizableText
      fontSize="$3"
      color={theme ? "#64748b" : "#94a3b8"}
      textAlign="center"
    >
      {label}
    </SizableText>
  </YStack>
);

export default WordpressOptions;
